import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch, useSelector } from 'react-redux';
import { Settings, Users } from 'lucide-react'; // Import the Settings icon
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../auth/AuthContext';
import {
  clearInfiniteStudents,
  clearInfiniteTeachers,
  getStudents,
  getTeachers
} from '../../../../store/teacherSlice.js';
import { DEFAULT_PAGE_NUMBER, ITEMS_PER_PAGE } from '../../../../utils/pagination.ts';
import { Loading } from '../../../Loading.js';
import { FiMousePointer } from 'react-icons/fi';

const AddDrop = props => {
  const { user } = useAuth();
  const dispatch = useDispatch();

  const [section, setSection] = useState('students');

  const [hovered, setHovered] = useState(null);
  const [pagingPage, setPagingPage] = useState(DEFAULT_PAGE_NUMBER);

  const {
    data: students,
    infiniteStudents,
    isLoading: isStudentsLoading
  } = useSelector(state => state?.teacherReducer?.students);

  const {
    data: teachers,
    infiniteTeachers,
    isLoading: isTeachersLoading
  } = useSelector(state => state?.teacherReducer?.teachers);

  // Check if the only student is the test student
  const hasOnlyTestStudent = () => {
    if (!infiniteStudents || infiniteStudents.length !== 1) {
      return false;
    }
    return infiniteStudents[0].email === 'student@test.com';
  };

  // Condition for showing animation for students - now includes the case where only student@test.com exists
  const showStudentAnimation =
    section === 'students' && !isStudentsLoading && (infiniteStudents ?? []).length === 0;

  // Condition for showing animation for teachers
  const showTeacherAnimation =
    section === 'teachers' && !isTeachersLoading && (infiniteTeachers ?? []).length === 0;

  useEffect(() => {
    try {
      getAllStudents(pagingPage);
      getAllTeachers(pagingPage);
    } catch (error) {
      console.error('Error fetching students or teachers:', error);
      // Continue with the component rendering even if fetching fails
    }

    return () => {
      dispatch(clearInfiniteStudents());
      dispatch(clearInfiniteTeachers());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPagingPage(1);
  }, [section]);

  const getAllStudents = page =>
    dispatch(
      getStudents({
        classId: props.class_id,
        token: user.session_token,
        search: '',
        pageNumber: page,
        itemsPerPage: ITEMS_PER_PAGE.default
      })
    );

  const getAllTeachers = page =>
    dispatch(
      getTeachers({
        classId: props.class_id,
        token: user.session_token,
        search: '',
        pageNumber: page,
        itemsPerPage: ITEMS_PER_PAGE.default
      })
    );

  return (
    <div className="add-drop-container h-screen flex flex-col bg-white border-l border-gray-200">
      <div className="sticky top-0 z-100 bg-white pt-6 pl-6 pr-6 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between mt-4">
          <h3 className="font-semibold text-gray-900">
            {section === 'students' ? 'Students' : 'Teachers'}
          </h3>
          <Link to={`/csettings/${props.class_id}`}>
            <button
              className="group flex items-center justify-center px-4 py-2 bg-white rounded-full
                      text-sm text-gray-700 border border-gray-200 hover:border-orange-200
                      hover:bg-orange-50 transition-all duration-200"
            >
              <Users className="w-5 h-5 text-gray-500 group-hover:text-orange-500 transition-colors mr-2" />
              <span>Manage Roster</span>
            </button>
          </Link>
        </div>
      </div>

      {section === 'students' ? (
        <div className="flex-1 overflow-y-auto px-6">
          <style jsx global>{`
            @keyframes subtle-pulse {
              0% {
                filter: drop-shadow(0 0 12px rgba(228, 177, 122, 0.4));
              }
              50% {
                filter: drop-shadow(0 0 21px rgba(228, 177, 122, 0.7));
              }
              100% {
                filter: drop-shadow(0 0 12px rgba(228, 177, 122, 0.4));
              }
            }

            .pointer-container {
              position: relative;
              animation: move-to-center 3s ease-in-out infinite;
            }

            @keyframes move-to-center {
              0% {
                transform: translate(20px, 20px);
              }
              40% {
                transform: translate(0, 0);
              }
              50% {
                transform: scale(0.9);
              }
              60% {
                transform: translate(0, 0) scale(1);
              }
              100% {
                transform: translate(20px, 20px);
              }
            }

            .mouse-pointer {
              filter: drop-shadow(0 0 1px rgba(228, 177, 122, 0.3));
              animation: pulse-opacity 2s ease-in-out infinite;
            }

            @keyframes pulse-opacity {
              0%,
              100% {
                opacity: 0.8;
              }
              50% {
                opacity: 1;
              }
            }

            .click-ripple-container {
              position: relative;
              animation: click-timing 3s infinite;
            }

            @keyframes click-timing {
              0%,
              45%,
              65%,
              100% {
                opacity: 0;
              }
              50%,
              55% {
                opacity: 1;
              }
            }

            .click-ripple {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: rgba(228, 177, 122, 0.6);
              border-radius: 50%;
              transform: scale(0);
              animation: click-ripple 1.5s infinite;
            }

            @keyframes click-ripple {
              0%,
              100% {
                transform: scale(0);
                opacity: 0;
              }
              10% {
                transform: scale(0);
                opacity: 0.8;
              }
              50% {
                transform: scale(10);
                opacity: 0;
              }
            }

            @keyframes fade-in {
              0% {
                opacity: 0;
              }
              100% {
                opacity: 1;
              }
            }
          `}</style>
          {(showStudentAnimation || showTeacherAnimation) && (
            <div
              style={{
                position: 'fixed',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                pointerEvents: 'none',
                zIndex: 50, // Below highlighted button, above everything else
                animation: 'fade-in 0.5s ease-in-out'
              }}
            ></div>
          )}
          {isStudentsLoading ? (
            <div className="flex justify-center items-center py-10">
              <Loading />
            </div>
          ) : (infiniteStudents ?? []).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="text-gray-700 mb-6 max-w-md">
                <h3 className="font-medium text-lg mb-2 text-gray-800">
                  There are no students in this course
                </h3>
                <p className="mb-3">Import a roster to:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Automatically match submissions to students</li>
                  <li>Email grades and feedback directly to students</li>
                </ul>
              </div>
              <Link to={`/csettings/${props.class_id}`}>
                <button
                  className="px-4 py-2 rounded-md transition-all duration-200 flex items-center relative"
                  style={{
                    position: 'relative',
                    background: 'white',
                    color: '#e4b17a',
                    boxShadow:
                      '0 4px 6px -1px rgba(228, 177, 121, 0.1), 0 2px 4px -1px rgba(228, 177, 121, 0.06)',
                    fontWeight: '500',
                    border: '1px solid #e4b17a',
                    ...(showStudentAnimation && {
                      filter: 'drop-shadow(0 0 30px rgba(228, 177, 122, 0.6))',
                      animation: 'subtle-pulse 2.5s infinite',
                      zIndex: 100 // Above overlay
                    })
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = '#f8f8f8';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Settings className="w-4 h-4 mr-2" style={{ color: '#e4b17a' }} />
                  Import Roster
                  {/* Add the animation elements only when showStudentAnimation is true */}
                  {showStudentAnimation && (
                    <div
                      className="new-roster-indicator"
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '60%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        zIndex: 20
                      }}
                    >
                      <div
                        className="pointer-container"
                        style={{ position: 'absolute', top: '23px' }}
                      >
                        <FiMousePointer
                          className="mouse-pointer"
                          size={24}
                          color="#e4b17a"
                          style={{
                            filter: 'drop-shadow(0 0 3px rgba(228, 177, 122, 0.7))',
                            transform: 'rotate(-5deg)'
                          }}
                        />
                      </div>

                      {/* Ripple effect directly over the button */}
                      <div
                        className="click-ripple-container"
                        style={{
                          position: 'absolute',
                          width: '24px',
                          height: '24px',
                          opacity: 0.9
                        }}
                      >
                        <div className="click-ripple"></div>
                      </div>
                    </div>
                  )}
                </button>
              </Link>
            </div>
          ) : hasOnlyTestStudent() ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="text-gray-700 mb-6 max-w-md">
                <h3 className="font-medium text-lg mb-2 text-gray-800">
                  This course only has the test student
                </h3>
                <p className="mb-3">Import a real roster to:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Automatically match submissions to students</li>
                  <li>Email grades and feedback directly to students</li>
                </ul>
              </div>
              <Link to={`/csettings/${props.class_id}`}>
                <button
                  className="px-4 py-2 rounded-md transition-all duration-200 flex items-center relative"
                  style={{
                    position: 'relative',
                    background: 'white',
                    color: '#e4b17a',
                    boxShadow:
                      '0 4px 6px -1px rgba(228, 177, 121, 0.1), 0 2px 4px -1px rgba(228, 177, 121, 0.06)',
                    fontWeight: '500',
                    border: '1px solid #e4b17a'
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = '#f8f8f8';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Settings className="w-4 h-4 mr-2" style={{ color: '#e4b17a' }} />
                  Import Roster
                </button>
              </Link>
            </div>
          ) : (
            <InfiniteScroll
              hasMore={!!students?.nextOffset}
              initialLoad={false}
              loader={!!isStudentsLoading ? <Loading /> : null}
              loadMore={async () => {
                if (!!students?.nextOffset && students?.nextOffset !== 0) {
                  getAllStudents(pagingPage + 1);
                  setPagingPage(pagingPage + 1);
                }
              }}
              useWindow={false}
              threshold={Number(ITEMS_PER_PAGE.default)}
            >
              {/* Student list */}
              {[
                ...new Map((infiniteStudents ?? []).map(student => [student.id, student])).values()
              ].map((student, index) => (
                <div key={index} className="student-item flex justify-between items-center py-3">
                  <div
                    className="relative cursor-pointer"
                    onMouseEnter={() => setHovered(student)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <span className="text-gray-700">
                      {student.first_name} {student.last_name}
                    </span>

                    {hovered === student && (
                      <div className="absolute z-10 bg-black text-white p-2 rounded text-sm mt-1 left-[110%] top-[-50%]">
                        ID: {student.id}
                        <br />
                        Email: {student.email}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </InfiniteScroll>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-6">
          {(showStudentAnimation || showTeacherAnimation) && (
            <div
              style={{
                position: 'fixed',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                pointerEvents: 'none',
                zIndex: 50, // Below highlighted button, above everything else
                animation: 'fade-in 0.5s ease-in-out'
              }}
            ></div>
          )}
          {isTeachersLoading ? (
            <div className="flex justify-center items-center py-10">
              <Loading />
            </div>
          ) : (infiniteTeachers ?? []).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10">
              <p className="text-gray-600 text-center mb-4">
                There are no teachers in this course.
              </p>
              <Link to={`/csettings/${props.class_id}`}>
                <button
                  className="px-4 py-2 rounded-md transition-all duration-200 flex items-center"
                  style={{
                    background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                    color: 'white',
                    boxShadow:
                      '0 4px 6px -1px rgba(228, 177, 121, 0.1), 0 2px 4px -1px rgba(228, 177, 121, 0.06)',
                    fontWeight: '500',
                    position: 'relative', // Needed for zIndex to work
                    ...(showTeacherAnimation && {
                      filter: 'drop-shadow(0 0 30px rgba(228, 177, 122, 0.6))',
                      animation: 'subtle-pulse 2.5s infinite',
                      zIndex: 100 // Above overlay
                    })
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #c99b69, #b38a5e)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #e4b17a, #c99b69)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Add Teachers
                </button>
              </Link>
            </div>
          ) : (
            <InfiniteScroll
              hasMore={!!teachers?.nextOffset}
              initialLoad={false}
              loader={!!isTeachersLoading ? <Loading /> : null}
              loadMore={async () => {
                if (!!teachers?.nextOffset && teachers?.nextOffset !== 0) {
                  getAllTeachers(pagingPage + 1);
                  setPagingPage(pagingPage + 1);
                }
              }}
              useWindow={false}
              threshold={Number(ITEMS_PER_PAGE.default)}
            >
              {(infiniteTeachers ?? []).map((teacher, index) => (
                <li key={index} className="student-item flex justify-between items-center py-3">
                  <div
                    className="relative cursor-pointer"
                    onMouseEnter={() => setHovered(teacher)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <span className="text-gray-700">{teacher.fullName}</span>

                    {hovered === teacher && (
                      <div className="absolute z-10 bg-black text-white p-2 rounded text-sm mt-1 left-[110%] top-[-50%]">
                        ID: {teacher.id}
                        <br />
                        Email: {teacher.email}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </InfiniteScroll>
          )}
        </div>
      )}
    </div>
  );
};

export default AddDrop;
